import type { PSP } from 'app/typings/PSP';
import type { BookingStatus } from 'app/typings/adminBookings/bookings';
import type { Refund } from 'app/typings/extranetBookings/booking';
import {
  BookingRefundStatus,
  BookingRefundType,
} from 'app/typings/extranetBookings/booking';
import type { IssuingTransaction } from 'app/typings/extranetBookings/issuing';
import {
  IssuingTransactionStatus,
  IssuingTransactionType,
} from 'app/typings/extranetBookings/issuing';
import type { VccStatusData, VccStatusDisplayInfo } from 'app/typings/vcc';
import { DEACTIVATE_AFTER_X_MONTHS, VccStatus } from 'app/typings/vcc';
import {
  addMonths,
  isAfter,
  isBefore,
  isFuture,
  isToday,
} from 'app/utils/dayjs';

import { dateAndHour } from './dates';
import { parseUppercaseSnakeTextToCapitalize } from './string';

export type BookingInfo = {
  checkin?: string;
  status: BookingStatus;
  psp?: PSP;
  refunds: Refund[];
  hotelAmount: number;
  issuingTransactionId?: string;
  issuingChargedAt?: string;
  issuingAuthorizationId?: string;
  vccStatus?: VccStatus;
  issuingTransactions?: IssuingTransaction[];
};

type VccHistoryType = { date: string; text: string }[];

export const getVCCStatus = (
  booking: BookingInfo,
  currencyFormatter: (value: number) => string
): VccStatusData => {
  const formattedCheckIn = booking.checkin
    ? new Date(booking.checkin)
    : new Date();
  const history = getVCCHistory(booking, currencyFormatter);

  if (booking.vccStatus) {
    switch (booking.vccStatus) {
      case VccStatus.Inactive:
        return {
          status: VccStatus.Inactive,
          text: parseUppercaseSnakeTextToCapitalize(VccStatus.Inactive),
          color: 'default',
          history,
        };
      case VccStatus.Cancelled:
        return {
          status: VccStatus.Cancelled,
          text: parseUppercaseSnakeTextToCapitalize(VccStatus.Cancelled),
          color: 'red',
          history,
        };
      case VccStatus.ToCharge:
        return {
          status: VccStatus.ToCharge,
          text: parseUppercaseSnakeTextToCapitalize(VccStatus.ToCharge),
          color: 'blue',
          history,
        };
      case VccStatus.Charged:
        return {
          status: VccStatus.Charged,
          text: parseUppercaseSnakeTextToCapitalize(VccStatus.Charged),
          color: 'green',
          history,
        };
      case VccStatus.ToRefund:
        return {
          status: VccStatus.ToRefund,
          text: parseUppercaseSnakeTextToCapitalize(VccStatus.ToRefund),
          color: 'gold',
          history,
        };
      case VccStatus.Deactivated:
        return {
          status: VccStatus.Deactivated,
          text: parseUppercaseSnakeTextToCapitalize(VccStatus.Deactivated),
          color: 'default',
          history,
        };
    }
  }

  const sumOfRefunds = getSumOfRefunds(
    booking.refunds.filter(
      (refund) => refund.status === BookingRefundStatus.ACCEPTED
    )
  );

  const allRefundsCharged = !booking.refunds.filter(
    (refund) => refund.status === BookingRefundStatus.PENDING_HOTEL
  ).length;

  const isFutureCheckIn =
    isFuture(formattedCheckIn) && !isToday(formattedCheckIn);
  const isFullyCharged =
    !!booking.issuingAuthorizationId && !booking.refunds.length;
  const isDeactivated =
    !isFullyCharged &&
    isAfter(new Date(), addMonths(formattedCheckIn, DEACTIVATE_AFTER_X_MONTHS));
  const isCancelled =
    !booking.issuingChargedAt &&
    booking.refunds.length === 1 &&
    sumOfRefunds === booking.hotelAmount &&
    allRefundsCharged;

  if (isCancelled) {
    return {
      status: VccStatus.Cancelled,
      text: parseUppercaseSnakeTextToCapitalize(VccStatus.Cancelled),
      color: 'red',
      history,
    };
  }

  if (isFutureCheckIn) {
    return {
      status: VccStatus.Inactive,
      text: parseUppercaseSnakeTextToCapitalize(VccStatus.Inactive),
      color: 'default',
      history,
    };
  }

  if (isDeactivated) {
    return {
      status: VccStatus.Deactivated,
      text: parseUppercaseSnakeTextToCapitalize(VccStatus.Deactivated),
      color: 'default',
      history,
    };
  }

  return {
    status: VccStatus.Inactive,
    text: parseUppercaseSnakeTextToCapitalize(VccStatus.Inactive),
    color: 'default',
    history,
  };
};

export const getVCCStatusDisplayInfo = (
  vccStatus: VccStatus
): VccStatusDisplayInfo => {
  const text = parseUppercaseSnakeTextToCapitalize(vccStatus);

  switch (vccStatus) {
    case VccStatus.Cancelled:
      return {
        text,
        color: 'red',
        status: vccStatus,
      };
    case VccStatus.Inactive:
      return {
        text,
        color: 'default',
        status: vccStatus,
      };
    case VccStatus.ToCharge:
      return {
        text,
        color: 'blue',
        status: vccStatus,
      };
    case VccStatus.Charged:
      return {
        text,
        color: 'green',
        status: vccStatus,
      };
    case VccStatus.ToRefund:
      return {
        text,
        color: 'gold',
        status: vccStatus,
      };
    case VccStatus.Deactivated:
      return {
        text,
        color: 'default',
        status: vccStatus,
      };

    default:
      return { text: 'Bank transfer', color: 'default', status: vccStatus };
  }
};

const addChargedAtToHistory = (
  transaction: IssuingTransaction,
  currencyFormatter: (value: number) => string
) => {
  const date =
    transaction.authorizedAt ||
    transaction.chargedAt ||
    new Date().toISOString();

  return {
    date: date,
    text: `${currencyFormatter(transaction.amount)} charged • ${dateAndHour(
      date
    )}`,
  };
};

const addRefundedToHistory = (
  transaction: IssuingTransaction,
  currencyFormatter: (value: number) => string
) => {
  return {
    date: transaction.chargedAt ?? new Date().toISOString(),
    text: `${currencyFormatter(
      transaction.amount
    )} refunded to VCC • ${dateAndHour(
      transaction.chargedAt ?? new Date().toISOString()
    )}`,
  };
};

const addRefundRequestedToHistory = (
  refund: Refund,
  currencyFormatter: (value: number) => string
) => {
  return {
    date: refund.createdAt,
    text: `Refund requested, amount due ${currencyFormatter(
      refund.refundedFromHotel ?? 0
    )} • ${dateAndHour(refund.createdAt)}`,
  };
};

const getVCCHistory = (
  booking: BookingInfo,
  currencyFormatter: (value: number) => string
): string[] => {
  const history: VccHistoryType = [];

  const chargedTransactions = booking.issuingTransactions?.filter(
    (transaction) =>
      transaction.status === IssuingTransactionStatus.PENDING ||
      (transaction.status === IssuingTransactionStatus.VALIDATED &&
        transaction.type === IssuingTransactionType.CAPTURE &&
        (transaction.authorizedAt || transaction.chargedAt))
  );

  chargedTransactions?.forEach((transaction) => {
    history.push(addChargedAtToHistory(transaction, currencyFormatter));
  });

  const refundedTransactions = booking.issuingTransactions?.filter(
    (transaction) =>
      (transaction.status === IssuingTransactionStatus.PENDING ||
        transaction.status === IssuingTransactionStatus.VALIDATED) &&
      transaction.type === IssuingTransactionType.REFUND &&
      transaction.chargedAt
  );

  refundedTransactions?.forEach((transaction) => {
    history.push(addRefundedToHistory(transaction, currencyFormatter));
  });

  const hotelRefunds = booking.refunds.filter(
    (refund) => refund.source === BookingRefundType.HOTEL.toUpperCase()
  );

  hotelRefunds.forEach((refund) => {
    history.push(addRefundRequestedToHistory(refund, currencyFormatter));
  });

  return history
    .sort((a, b) => (isBefore(new Date(a.date), new Date(b.date)) ? -1 : 1))
    .map((entry) => entry.text);
};

// we multiply by 100 and divide by 100 to avoid floating point issues with float numbers
export const getSumOfRefunds = (refunds: Refund[]): number => {
  return (
    refunds.reduce((total, refund) => {
      return total + (refund.refundedFromHotel ?? 0) * 100;
    }, 0) / 100
  );
};
